import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import createAxiosInstance from "../utils/axiosConfig";
import { userDataProps } from "../utils/interfaces";

interface AuthContextType {
	isAuthenticated: boolean;
	isLoading: boolean;
	userId: number | null;
	userData: userDataProps | null;
	token: string | null;
	login: (userId: number, token: string, userData: userDataProps) => void;
	logout: () => void;
	checkAuthStatus: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [userId, setUserId] = useState<number | null>(null);
	const [token, setToken] = useState<string | null>(null);
	const [userData, setUserData] = useState<userDataProps | null>(null);
	const lastAuthCheck = useRef<number>(0);
	const AUTH_CHECK_COOLDOWN = 3000; // 3 seconds cooldown between auth checks
	const isInitialized = useRef<boolean>(false);
	const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
	const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes of inactivity

	// Initialize auth state from localStorage on mount only
	useEffect(() => {
		const storedUserId = localStorage.getItem("userId");
		const storedToken = localStorage.getItem("token");
		const storedUserInfo = localStorage.getItem("userData");
		const userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : null;

		if (storedUserId && storedToken) {
			setIsAuthenticated(true);
			setUserId(Number(storedUserId));
			setToken(storedToken);
			setUserData(userInfo);
			// Only check auth status once on initial load if we have stored credentials
			checkAuthStatus();
		} else {
			setIsAuthenticated(false);
		}
		isInitialized.current = true;
		setIsLoading(false);
	}, []); // Empty dependency array - only run on mount

	const checkAuthStatus = useCallback(async () => {
		const now = Date.now();
		
		// Prevent excessive API calls with cooldown
		if (now - lastAuthCheck.current < AUTH_CHECK_COOLDOWN) {
			console.log('Auth check skipped due to cooldown');
			return;
		}
		
		lastAuthCheck.current = now;
		
		try {
			const response = await createAxiosInstance().get("/me");
			// Handle successful response if needed
			if (response.status == 401 || response.data == null) {
				logout();
				window.location.href = "/login";
				return;
			}
			setUserData(response.data);
		} catch (error: any) {
			// If error response is 404 or unauthorized, logout and redirect
			if (
				error.response &&
				(error.response.status === 404 || error.response.status === 401)
			) {
				logout();
				window.location.href = "/login";
			}
		}
	}, []);

	// Listen for token refresh events from axios interceptor
	useEffect(() => {
		const handleTokenRefresh = (event: any) => {
			const { newToken } = event.detail;
			console.log('Token refreshed automatically:', newToken);
			setToken(newToken);
			
			// Update localStorage with the new token (already done in axios interceptor, but for consistency)
			localStorage.setItem("token", newToken);
			
			// Don't call checkAuthStatus here to avoid infinite loops
			// The token refresh itself indicates the user is authenticated
			// We'll only refresh user data if specifically needed
		};

		window.addEventListener('tokenRefreshed', handleTokenRefresh);

		return () => {
			window.removeEventListener('tokenRefreshed', handleTokenRefresh);
		};
	}, []); // Remove checkAuthStatus from dependencies

	// Define logout function before it's used in useEffect
	const logout = useCallback(() => {
		setIsAuthenticated(false);
		setUserId(null);
		setToken(null);
		localStorage.removeItem("userId");
		localStorage.removeItem("token");
		localStorage.removeItem("userData");
		
		// Clear idle timer on logout
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
			idleTimerRef.current = null;
		}
	}, []);

	// NOTE: Removed automatic logout on browser close/tab close
	// Modern web applications typically persist login state across browser sessions
	// and rely on server-side token expiration for security
	// The previous implementation was causing race conditions with page refreshes

	// Idle timeout - automatically log out after 5 minutes of inactivity
	useEffect(() => {
		if (!isAuthenticated) {
			// Clear timer if user is not authenticated
			if (idleTimerRef.current) {
				clearTimeout(idleTimerRef.current);
				idleTimerRef.current = null;
			}
			return;
		}

		const resetIdleTimer = () => {
			// Clear existing timer
			if (idleTimerRef.current) {
				clearTimeout(idleTimerRef.current);
			}

			// Set new timer
			idleTimerRef.current = setTimeout(() => {
				console.log('Session expired due to inactivity');
				logout();
				window.location.href = '/login?reason=timeout';
			}, IDLE_TIMEOUT);
		};

		// Initialize timer
		resetIdleTimer();

		// Events that indicate user activity
		const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'mousemove', 'click'];

		// Add event listeners for all activity events
		activityEvents.forEach(event => {
			document.addEventListener(event, resetIdleTimer, true);
		});

		// Cleanup function
		return () => {
			// Remove all event listeners
			activityEvents.forEach(event => {
				document.removeEventListener(event, resetIdleTimer, true);
			});

			// Clear timer
			if (idleTimerRef.current) {
				clearTimeout(idleTimerRef.current);
				idleTimerRef.current = null;
			}
		};
	}, [isAuthenticated, logout, IDLE_TIMEOUT]);

	const login = (id: number, jwtToken: string, userData: userDataProps) => {
		setIsAuthenticated(true);
		setUserId(Number(id));
		setToken(jwtToken);
		setUserData(userData);
		localStorage.setItem("userId", `${id}`);
		localStorage.setItem("token", jwtToken);
		localStorage.setItem("userData", JSON.stringify(userData));
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				isLoading,
				userId,
				userData,
				token,
				login,
				logout,
				checkAuthStatus,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export { AuthProvider, useAuth };
